import React, { useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useFetchApi from '../../../../../../hooks/useFetchApi';
import { SectorByIdSchemaResponse } from '../../../../../../requests/api/apiTypes';
import SoilDashboard from './soilDashboard';
import PlantDashboard from './plantDashboard';
import { ReduxState } from '../../../../../../types';
import { setDashboardType } from '../../../../../../actions/farmSectorActions';
import SoilTemperatureSalinityDashboard from './soilTemperatureSalinityDashboard';
import WeatherDashboard from './weatherDashboard';
import SoilChartsTab from '../SectorCharts/SoilChartsTab';
import IrrigationChartsTab from '../SectorCharts/IrrigationChartsTab';
import WeatherChartsTab from '../SectorCharts/WeatherChartsTab';
import PlantsChartTab from '../SectorCharts/PlantsChartTab';
import { useChartExceptionContext } from '../../../../../../context/ChartExceptionContext';

const Dashboards = ({
  sectorId,
}: {
  sectorId: string,
}) => {
  const { search } = useLocation();
  const dispatch = useDispatch();
  const dashboardType = useSelector((state: ReduxState) => state.farmSector.dashboardType ?? 1);
  const chartExceptionContext = useChartExceptionContext();
  const dataRefreshKey = chartExceptionContext?.dataRefreshKey ?? 0;
  useEffect(() => {
    const params = new URLSearchParams(search);
    const type = params.get('type');
    if (!type) return;
    switch (type) {
      case 'planta':
        dispatch(setDashboardType(2));
        break;
      case 'suelo':
        dispatch(setDashboardType(1));
        break;
      case 'suelo-salinidad-temperatura':
        dispatch(setDashboardType(3));
        break;
      case 'clima':
        dispatch(setDashboardType(4));
        break;
      case 'suelo-todos':
        dispatch(setDashboardType(5));
        break;
      case 'planta-todos':
        dispatch(setDashboardType(6));
        break;
      case 'riego-todos':
        dispatch(setDashboardType(7));
        break;
      case 'clima-todos':
        dispatch(setDashboardType(8));
        break;
      default:
        dispatch(setDashboardType(1));
    }
  }, [search, dispatch]);

  const { data: fetchData, loading } = useFetchApi<SectorByIdSchemaResponse>({
    modelEndpoint: 'sectorDetail',
    modelName: 'sector',
    id: sectorId,
  });

  const farmId = fetchData?.sector?.farm || '';

  const hasValidTelemetry = fetchData?.sector?.wiseconnId
    || (fetchData?.sector as any)?.sn;

  const dashboardContent = loading ? (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <CircularProgress />
    </Box>
  ) : ((() => {
    switch (dashboardType) {
      case 1:
        return hasValidTelemetry ? (<SoilDashboard
          sectorId={sectorId}
          farmId={farmId}
        />) : null;
      case 2:
        return hasValidTelemetry ? (<PlantDashboard
          sectorId={sectorId}
          farmId={farmId}
        />) : null;
      case 3:
        return hasValidTelemetry ? (<SoilTemperatureSalinityDashboard
          sectorId={sectorId}
          farmId={farmId}
        />) : null;
      case 4:
        return hasValidTelemetry ? (<WeatherDashboard
          sectorId={sectorId}
          farmId={farmId}
        />) : null;
      case 5:
        return hasValidTelemetry ? (<SoilChartsTab
          sectorId={sectorId}
          farmId={farmId}
        />) : null;
      case 6:
        return hasValidTelemetry ? (<PlantsChartTab
          sectorId={sectorId}
          farmId={farmId}
        />) : null;
      case 7:
        return hasValidTelemetry ? (<IrrigationChartsTab
          sectorId={sectorId}
          farmId={farmId}
        />) : null;
      case 8:
        return hasValidTelemetry ? (<WeatherChartsTab
          sectorId={sectorId}
          farmId={farmId}
        />) : null;
      default:
        return null;
    }
  })());

  return <React.Fragment key={dataRefreshKey}>{dashboardContent}</React.Fragment>;
};
export default Dashboards;
